import React, { ReactNode } from 'react';
import styled from 'styled-components';

import { WhitePanel } from 'components/Toolkit/WhitePanel/WhitePanel';
import { ButtonLink } from 'components/Toolkit/Button/ButtonLink';
import { MiniUserProfile } from 'components/Toolkit/MiniUserProfile/MiniUserProfile';
import { InfoTitle } from 'components/Toolkit/InfoTitle/InfoTitle';

import { VerifiedList } from 'components/PageWrappers/AdDetails/components/VerifiedList/VerifiedList';
import { formatVerification } from 'helpers/formatting';
import type { UserVerification } from 'components/PageWrappers/AdDetails/types';
import { StarRatingGroup } from 'components/PageWrappers/AdDetails/components/GoogleReviews/StarRatingGroup/StarRatingGroup';
import { media } from 'helpers/breakpoints';

export interface SideSellerInfoPanelProps {
  title?: string;
  renderSendMessage?: ReactNode;
  renderShowPhone?: ReactNode;
  renderMakeOffer?: ReactNode;
  county?: string;
  type?: string;
  sellerUrl?: string;
  isTrader: boolean;
  verification?: UserVerification;
  className?: string;
  testId?: string;
  isDealer?: boolean;
  showroomUrl?: string;
  illustration?: string;
  dealerReviews?: string;
  dealerRating?: string;
}

const MiniUserProfileAndVerifiedListContainer = styled.div<{
  googleReviews?: any;
}>`
  margin-bottom: ${({ theme, googleReviews }) =>
    googleReviews ? `0` : theme.spacing.M16};
`;

const ButtonList = styled.ul`
  display: flex;
  flex-direction: column;
`;

const ButtonItem = styled.li`
  margin-bottom: ${({ theme }) => theme.spacing.S8};

  &:last-child {
    margin: 0;
  }
`;

const DealerRatings = styled.div`
  margin-bottom: 0;
`;

const DealerReviewInfoTitle = styled(InfoTitle)`
  ${media.large} {
    min-height: unset;
    margin-bottom: ${({ theme }) => theme.spacing.S8};
    margin-top: ${({ theme }) => theme.spacing.S8};
  }
`;

function SideSellerInfoPanel({
  title,
  renderSendMessage,
  renderShowPhone,
  renderMakeOffer,
  county,
  type,
  sellerUrl,
  isTrader,
  verification,
  className,
  testId,
  isDealer,
  showroomUrl,
  illustration,
  dealerReviews,
  dealerRating,
}: SideSellerInfoPanelProps) {
  return (
    <WhitePanel className={className} testId={testId}>
      <MiniUserProfileAndVerifiedListContainer googleReviews={dealerReviews}>
        {title && (
          <MiniUserProfile
            title={title}
            illustration={illustration}
            county={county}
            type={type}
            isTrader={isTrader}
            verification={verification}
            isDealer={isDealer}
            position="SIDE"
          />
        )}
        {!isDealer && verification && (
          <VerifiedList verification={formatVerification(verification)} />
        )}
      </MiniUserProfileAndVerifiedListContainer>
      {dealerReviews && (
        <DealerRatings>
          {dealerRating && (
            <>
              <DealerReviewInfoTitle
                title={''}
                listItems={[
                  <StarRatingGroup
                    key={1}
                    rating={parseFloat(dealerRating)}
                    totalReviews={dealerReviews}
                    clickTargetElementId="dealer-panel"
                    starWidth="20"
                    starHeight="20"
                    totalReviewsTrackingId={'google-reviews-total-reviews'}
                  />,
                ]}
              />
            </>
          )}
        </DealerRatings>
      )}
      <ButtonList>
        {renderMakeOffer && <ButtonItem>{renderMakeOffer}</ButtonItem>}
        {renderSendMessage && <ButtonItem>{renderSendMessage}</ButtonItem>}
        {renderShowPhone && <ButtonItem>{renderShowPhone}</ButtonItem>}

        {sellerUrl && (
          <ButtonItem>
            <ButtonLink
              href={showroomUrl || sellerUrl}
              ofType="SECONDARY"
              data-testid="view-all-ads"
              data-tracking="view-all-ads-button"
              rel={isDealer ? undefined : 'nofollow'}
            >
              {isDealer
                ? 'Visit Showroom'
                : isTrader
                ? 'View Our Shop'
                : 'View All Ads'}
            </ButtonLink>
          </ButtonItem>
        )}
      </ButtonList>
    </WhitePanel>
  );
}

export { SideSellerInfoPanel };
